import React from 'react';
import { Crown, Clock, Package, Percent, TrendingUp, BookOpen, Check, Play, Calendar, ArrowRight, AlertTriangle, Upload, MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import CurrencyToggle from '../CurrencyToggle';
import ClassScheduleView from '../ClassScheduleView';
import { PRICING_TABS, PAYMENT_INFO, TAB_CATEGORIES, TAB_ACTIVE_COLOR, ENROLLMENT_EMAIL, openHotmart } from './coursesConstants';

const discountLabels = {
  conselhos: 'Profissionais de Conselhos, associações e sindicatos conveniados',
  estudantes: 'Estudantes de instituições de ensino conveniadas'
};

export const ExpertBundleCard = ({
  bundle, fmt, currency, expertTab, setExpertTab, schedules,
  bundleCheckout, courses, navigate, onEnroll, onEnrollmentRequest, onConsult,
}) => {
  const individualValueFor = (tabValue) => {
    const base = PRICING_TABS.find((t) => t.value === tabValue)?.base;
    if (base === 'ead_live') return bundle.prices.ead_live;
    if (base === 'presential') return bundle.prices.presential;
    return bundle.individual_total;
  };

  return (
    <Card className="bg-gradient-to-br from-red-900/30 via-zinc-900 to-zinc-900 border-2 border-red-500 overflow-hidden relative">
      <div className="absolute top-4 right-4">
        <Badge className="bg-red-600 text-white text-lg px-4 py-2">
          <Crown className="mr-2" size={16} />
          DESTAQUE
        </Badge>
      </div>

      <CardHeader className="pb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <CardTitle className="text-3xl text-white mb-2">{bundle.title}</CardTitle>
            <CardDescription className="text-gray-300 text-lg">
              {bundle.description}
            </CardDescription>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-black/30 rounded-lg p-4 text-center">
            <Clock className="text-red-500 mx-auto mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{bundle.total_hours}h</div>
            <div className="text-xs text-gray-400">Carga Horária</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4 text-center">
            <Package className="text-red-500 mx-auto mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{bundle.course_ids.length}</div>
            <div className="text-xs text-gray-400">Cursos Inclusos</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4 text-center">
            <Percent className="text-red-500 mx-auto mb-2" size={24} />
            <div className="text-2xl font-bold text-green-400">{bundle.savings_percentage}%</div>
            <div className="text-xs text-gray-400">Economia</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4 text-center">
            <TrendingUp className="text-red-500 mx-auto mb-2" size={24} />
            <div className="text-lg font-bold text-white line-through opacity-50">
              {fmt(individualValueFor(expertTab))}
            </div>
            <div className="text-xs text-gray-400">Valor Individual</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {bundle.id === 6 && (
          <div
            className="bg-amber-500/10 border border-amber-400/40 rounded-lg p-4 flex items-start gap-3"
            data-testid="expert-access-notice"
          >
            <AlertTriangle className="text-amber-400 mt-0.5 shrink-0" size={20} />
            <p className="text-amber-100/90 text-sm leading-relaxed">
              <strong className="text-amber-300">Matrícula liberada.</strong> Nesta primeira fase, o acesso contempla o conteúdo até o módulo <strong>Revit | Edificação Multifamiliar</strong>. Os demais módulos serão liberados progressivamente, com a formação completa disponível em <strong>dezembro de 2026</strong>.
            </p>
          </div>
        )}
        <div className="bg-black/20 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3 flex items-center">
            <BookOpen size={18} className="mr-2 text-red-500" />
            Cursos Incluídos nesta Formação
          </h4>
          <div className="grid md:grid-cols-2 gap-2">
            {bundle.syllabus && bundle.syllabus.length > 0 ? (
              bundle.syllabus.map((item, index) => (
                <div key={`syllabus-${item}-${index}`} className="flex items-center space-x-2 text-sm text-gray-300">
                  <Check size={14} className="text-green-400 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))
            ) : (
              courses.filter(c => bundle.course_ids.includes(c.id)).map((course, index) => (
                <div key={course.id} className="flex items-center space-x-2 text-sm text-gray-300">
                  <Check size={14} className="text-green-400 flex-shrink-0" />
                  <span>{course.title} ({course.duration})</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {bundle.features.map((feature, index) => (
            <div key={`feature-${feature}-${index}`} className="flex items-start space-x-2 bg-black/20 rounded-lg p-3">
              <Check size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-200">{feature}</span>
            </div>
          ))}
        </div>

        <Tabs value={expertTab} onValueChange={setExpertTab} className="w-full">
          <div className="text-center mb-4" data-testid="choose-category-cta">
            <h4 className="text-lg md:text-xl font-bold text-white">Escolha a sua categoria</h4>
            <p className="text-sm text-gray-400">Selecione o perfil que melhor se encaixa e veja os valores e condições de pagamento.</p>
          </div>
          <TabsList className="flex flex-col gap-3 bg-transparent h-auto p-0 mb-6">
            {TAB_CATEGORIES.map((cat) => (
              <div key={cat.key} className="w-full">
                <div className="flex items-center gap-2 mb-1.5 pl-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {cat.label}
                  </span>
                  {cat.key !== 'padrao' && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${cat.key === 'conselhos' ? 'bg-teal-500/20 text-teal-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      Descontos Exclusivos <span className="font-medium normal-case opacity-90">(mediante comprovação de vínculo)</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap justify-center gap-1 bg-zinc-800 rounded-lg p-1">
                  {PRICING_TABS.filter((t) => t.category === cat.key).map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      disabled={currency !== 'BRL' && tab.discount > 0}
                      className={`${TAB_ACTIVE_COLOR[tab.color]} data-[state=active]:text-white disabled:opacity-40 disabled:cursor-not-allowed grow-0 shrink-0 basis-[46%] sm:basis-[190px]`}
                    >
                      <div className="text-center py-1">
                        <div className="font-semibold text-sm leading-tight">{tab.label}</div>
                        <div className="text-xs opacity-80">{tab.sub}</div>
                      </div>
                    </TabsTrigger>
                  ))}
                </div>
              </div>
            ))}
          </TabsList>
          {PRICING_TABS.map((tab) => {
            const nextSchedule = schedules[`${bundle.id}_${tab.base}`]?.[0];
            const discount = tab.discount;
            const basePrice = bundle.prices[tab.base];
            const modPrice = basePrice * (1 - discount);
            const isEad = tab.kind === 'ead';
            const isBaseMentor = tab.kind === 'mentor' && discount === 0;
            const showPrice = basePrice > 0;
            const payInfo = PAYMENT_INFO[tab.kind] || PAYMENT_INFO.ead;
            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-4">
                <div className="bg-zinc-800/50 rounded-xl p-6">
                  {tab.kind === 'live' && nextSchedule && (
                    <div className="mb-6 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Calendar className="text-green-400 mt-1" size={20} />
                        <div className="flex-1">
                          <div className="text-green-400 font-semibold mb-1">
                            🎯 Próxima Turma
                          </div>
                          <div className="text-white font-medium">{nextSchedule.class_name}</div>
                          <div className="text-sm text-gray-300 mt-1">
                            📅 Início: {new Date(nextSchedule.start_date).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isEad && (
                    <div className="mb-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Play className="text-blue-400 mt-1" size={20} />
                        <div className="flex-1">
                          <div className="text-blue-400 font-semibold mb-1">
                            ⚡ Acesso Imediato
                          </div>
                          <div className="text-sm text-gray-300">
                            • Comece hoje mesmo<br/>
                            • Estude no seu ritmo<br/>
                            • Acesso vitalício ao conteúdo
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {showPrice ? (
                    <>
                      <div className="text-center mb-6">
                        {discount > 0 && tab.noteType && (
                          <div className={`inline-block mb-3 px-3 py-1 rounded-full text-xs font-semibold ${tab.color === 'teal' ? 'bg-teal-500/15 border border-teal-400/40 text-teal-300' : 'bg-blue-500/15 border border-blue-400/40 text-blue-300'}`}>
                            {discountLabels[tab.noteType]} ({discount * 100}% OFF)
                          </div>
                        )}
                        <div className="flex justify-center mb-1" data-testid="currency-toggle-wrapper">
                          <div className="scale-[0.6] origin-center">
                            <CurrencyToggle compact />
                          </div>
                        </div>
                        <div className="text-sm text-gray-400 mb-2">Investimento</div>
                        {discount > 0 && isEad && (
                          <div className="text-gray-500 line-through text-lg">
                            {fmt(basePrice)}
                          </div>
                        )}
                        <div className="flex items-center justify-center gap-3 mb-2 flex-wrap">
                          <span className="text-5xl font-bold text-white">{fmt(modPrice)}</span>
                        </div>
                        {currency === 'BRL' && (
                          <>
                            <div className="text-gray-400">
                              ou {payInfo.count}x de {fmt(tab.parcela != null ? tab.parcela : (discount > 0 ? modPrice / payInfo.count : payInfo.value))}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Pagamento em {payInfo.methods}
                            </div>
                          </>
                        )}
                        {currency !== 'BRL' && (
                          <div className="inline-flex items-center gap-1.5 mt-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-400/30 rounded-full px-3 py-1" data-testid="eur-international-note">
                            <ExternalLink size={12} />
                            Pagamento internacional via PayPal
                          </div>
                        )}
                        {discount > 0 && isEad && (
                          <div className="text-green-400 font-semibold text-lg mt-2">
                            Você economiza {fmt(basePrice - modPrice)} ({discount * 100}% OFF)
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Button
                          onClick={() => navigate(`/formation/${bundle.id}`)}
                          variant="outline"
                          size="lg"
                          className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                        >
                          <BookOpen className="mr-2" size={18} />
                          Ver Programa Completo
                        </Button>

                        {tab.value === 'ead_recorded' ? (
                          <Button
                            onClick={() => {
                              const cfg = bundleCheckout[bundle.id];
                              if (cfg?.padrao) openHotmart(cfg.padrao);
                              else onEnroll(bundle, tab.label, tab.base);
                            }}
                            size="lg"
                            className="w-full bg-red-600 hover:bg-red-700 text-white text-lg group"
                            data-testid="enroll-ead_recorded-btn"
                          >
                            Matricular na Formação Completa
                            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                          </Button>
                        ) : discount > 0 ? (
                          <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-4 text-left" data-testid={`enroll-docs-${tab.value}`}>
                            <div className="flex items-start gap-3 mb-3">
                              <AlertTriangle className="text-amber-400 mt-0.5 shrink-0" size={18} />
                              <p className="text-amber-100/90 text-xs leading-relaxed">
                                Esta condição <strong>não possui checkout direto</strong>. O valor com desconto é liberado <strong>após a comprovação de vínculo</strong> com a instituição/conselho conveniado. A matrícula é feita mediante o envio dos documentos abaixo.
                              </p>
                            </div>
                            <p className="font-semibold text-white text-sm mb-1">Documentos necessários:</p>
                            <ul className="text-amber-100/90 text-xs list-disc pl-5 space-y-0.5 mb-3">
                              <li>Cópia do documento de identidade (RG ou CNH)</li>
                              <li>CPF</li>
                              <li>Comprovante de residência</li>
                              <li>Comprovante de vínculo atualizado (ano vigente) com a instituição/conselho conveniado</li>
                            </ul>
                            <Button
                              type="button"
                              onClick={() => onEnrollmentRequest({ bundle, label: tab.label })}
                              size="lg"
                              className="w-full bg-red-600 hover:bg-red-700 text-white group"
                              data-testid={`enroll-${tab.value}-btn`}
                            >
                              <Upload className="mr-2" size={18} />
                              Solicitar matrícula (enviar documentos)
                            </Button>
                            <p className="text-xs text-gray-400 mt-2 text-center">
                              Preencha seus dados e anexe os documentos com segurança. Dúvidas: <strong className="text-gray-200">{ENROLLMENT_EMAIL}</strong>
                            </p>
                          </div>
                        ) : (
                          <Button
                            onClick={() => onConsult(bundle)}
                            size="lg"
                            className="w-full bg-red-600 hover:bg-red-700 text-white text-lg"
                            data-testid={`consult-${tab.value}-btn`}
                          >
                            <MessageSquare className="mr-2" size={18} />
                            Agende uma conversa
                          </Button>
                        )}
                      </div>
                    </>
                  ) : isBaseMentor ? (
                    <div className="text-center py-10" data-testid="mentor-em-breve">
                      <Badge className="bg-zinc-700 text-gray-200 text-lg px-6 py-2">
                        EM BREVE
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-sm text-gray-400 mb-2">Investimento</div>
                      <div className="text-2xl font-bold text-white mb-2">Valores sob consulta</div>
                      <p className="text-gray-400 text-sm mb-6">
                        Esta modalidade tem condições personalizadas. Fale com um de nossos consultores.
                      </p>
                      <div className="space-y-3">
                        <Button
                          onClick={() => navigate(`/formation/${bundle.id}`)}
                          variant="outline"
                          size="lg"
                          className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                        >
                          <BookOpen className="mr-2" size={18} />
                          Ver Programa Completo
                        </Button>
                        <Button
                          onClick={() => onConsult(bundle)}
                          size="lg"
                          className="w-full bg-red-600 hover:bg-red-700 text-white text-lg"
                          data-testid="consult-modality-btn"
                        >
                          <MessageSquare className="mr-2" size={18} />
                          Agende uma conversa
                        </Button>
                      </div>
                    </div>
                  )}

                  {tab.kind === 'live' && (
                    <div className="mt-6">
                      {schedules[`${bundle.id}_${tab.base}`]?.map((schedule, idx) => (
                        <ClassScheduleView key={idx} schedule={schedule} />
                      ))}
                      {!schedules[`${bundle.id}_${tab.base}`] && (
                        <div className="text-center text-gray-400 mt-4 p-4 bg-zinc-800 rounded-lg">
                          <p>Novas turmas em breve!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ExpertBundleCard;
